import { useTranslations } from "use-intl";
import { useNavigate } from "@tanstack/react-router";
import { AirplaneTiltIcon, BuildingsIcon } from "@phosphor-icons/react";
import {
  rudderAnalytics,
  HotelTrackingEvents,
  FlightTrackingEvents,
  TripTrackingEvents,
} from "@/app/lib/rudderstack";
import { IconTrips } from "@/app/icons";
import type { SearchScope } from "@/app/lib/types/types";
import { Feature, isFeatureDisabled } from "@/app/lib/utils/featureFlags";
import { ScopeSwitcherBase, ScopeSwitcherButton } from "../ScopeSwitcherBase";

interface CategorySwitcherProps {
  current: SearchScope;
}

const eventMap = {
  flights: FlightTrackingEvents.USER_SEARCHED_FOR_ORDERS,
  hotels: HotelTrackingEvents.USER_SEARCHED_FOR_ORDERS,
  trips: TripTrackingEvents.USER_SEARCHED_FOR_ORDERS,
} as const;

export const CategorySwitcher = ({ current }: CategorySwitcherProps) => {
  const t = useTranslations();
  const navigate = useNavigate();

  return (
    <ScopeSwitcherBase
      color="secondary"
      value={current}
      onChange={(newValue) => {
        if (newValue && eventMap[newValue]) {
          rudderAnalytics.track(eventMap[newValue]);
        }
        navigate({
          to: "/bookings",
          search: {
            category: newValue || current,
          },
          replace: true,
        });
      }}
    >
      <ScopeSwitcherButton value="flights">
        <AirplaneTiltIcon size={48} aria-hidden className="shrink-0 p-3" />
        {t("flights.title")}
      </ScopeSwitcherButton>

      <ScopeSwitcherButton value="hotels">
        <BuildingsIcon size={48} aria-hidden className="shrink-0 p-3" />
        {t("hotels.title")}
      </ScopeSwitcherButton>

      {isFeatureDisabled(Feature.TripsFlow) ? null : (
        <ScopeSwitcherButton value="trips">
          <IconTrips aria-hidden className="size-12 shrink-0 p-3" />
          {t("trips.title")}
        </ScopeSwitcherButton>
      )}
    </ScopeSwitcherBase>
  );
};
